/**
 * Typed access to the published glossary.
 *
 * `generated/glossary.generated.ts` is written by `context/scripts/publish-glossary.mjs`
 * from `docs/glossary.json` and is committed. Nothing outside this module should import
 * the generated file directly: this is where the untyped JSON becomes a typed shape and
 * where lookup lives, so a change to the artifact's layout lands in one place — the same
 * arrangement as `docs/index.ts` for guides.
 *
 * The glossary is the single definition of every term of art (working-conventions §44).
 * Four surfaces read it — this page, guide prose, tooltips, and search/Ask retrieval — so
 * a definition is written once and never retyped at a call site.
 */
import { FEATURE_SWITCHES_OFF, type FeatureSwitches } from 'constants/featureSwitches'

import { withoutGated } from './featureGate'
import { GLOSSARY_ENTRIES } from './generated/glossary.generated'
import { GLOSSARY_ENTRIES_INTERNAL } from './generated/glossary-internal.generated'

/** What kind of thing is being defined. Drives grouping on the glossary page. */
export type GlossaryKind = 'term' | 'column' | 'formula' | 'status'

/**
 * A cross-cutting label, orthogonal to `kind`.
 *
 * `kind` is a HOME — one per entry, and what the page groups by. A tag is a second axis,
 * so an entry keeps its home and still appears in the index. `metric` exists because the
 * figures on Analytics are not one sort of thing: Scans and Certificates are domain nouns
 * (`kind: 'term'`) that happen to be KPI tiles, while Invalid scan rate is a computed
 * figure and nothing else (`kind: 'formula'`). A "Metrics" SECTION would have forced each
 * of those to leave the group a reader would look for it in.
 *
 * See `publish-glossary.mjs` for why the set is closed.
 */
export type GlossaryTag = 'metric'

export interface GlossaryEntry {
  /** Stable kebab-case id. It is the `#anchor` AND the key tooltips reference. */
  id: string
  term: string
  /** Other spellings a reader might search for. Unique across the whole glossary. */
  aliases?: readonly string[]
  kind: GlossaryKind
  /**
   * The LEAST privileged reader allowed to see this entry, not a label for who it is
   * written for. The audiences nest — `consumer` ⊂ `brand` ⊂ `internal` — so a `consumer`
   * entry appears on this (brand) glossary too. See `publish-glossary.mjs` for the model.
   */
  audience: 'consumer' | 'brand' | 'internal'
  /**
   * ONE sentence, capped at 180 chars by the publisher. This is the tooltip body — see
   * §44 for why the cap is load-bearing rather than an inconvenience.
   */
  short: string
  /** Optional detail, glossary page only. Never shown in a tooltip. */
  long?: string
  /** `kind: 'formula'` only — the expression itself. */
  formula?: string
  unit?: string
  /** Ids of related entries. Build-enforced to resolve, so a link never dangles. */
  seeAlso?: readonly string[]
  /**
   * VRG-680 — the launch switch this entry is gated on. Dropped from the BRAND corpus while
   * that switch is off (`glossaryFor`); the staff corpus is never gated.
   */
  feature?: string
  /** `category/slug[#heading]` references into the guides. */
  docs?: readonly string[]
  /**
   * Cross-cutting labels. An entry keeps its `kind` grouping and additionally appears in
   * each tagged index on the glossary page. Validated against a closed set at publish
   * time.
   */
  tags?: readonly GlossaryTag[]
}

export const GLOSSARY = GLOSSARY_ENTRIES as readonly GlossaryEntry[]

/**
 * The staff corpus: everything in `GLOSSARY` plus the `audience: internal` entries.
 *
 * This exists because until VRG-426 it did not, and that was the defect §59 names by its
 * file name. `glossary-internal.generated.ts` was published on every run and imported by
 * NOTHING except the audience guard — so the artifact was correct, committed, and
 * unreachable. Its sole internal term could not be looked up by staff, by brands, or by
 * anyone: `<InfoTooltip term="tag-family">` resolved to `undefined` and rendered no
 * tooltip at all, silently, because a `term` that does not resolve falls back to the
 * literal `text` and a term-only tooltip has none.
 *
 * Prefer `useGlossaryTerm` / `useGlossaryCorpus` (see `GlossaryProvider`) over reaching
 * for this directly. Which corpus a reader gets is a function of WHO IS READING, and that
 * question has one answer per render, not per call site.
 */
export const INTERNAL_GLOSSARY = GLOSSARY_ENTRIES_INTERNAL as readonly GlossaryEntry[]

/**
 * Pick the corpus for a viewer.
 *
 * Note the direction of the default: anything that cannot answer "is this Vera staff"
 * gets the BRAND corpus. Failing closed is the whole point — a wrong answer here is
 * either a term that fails to render (recoverable, visible) or an internal term shown to
 * a brand (a leak, and the thing §43/§44 exist to prevent). Only one of those is worth
 * risking on a default.
 */
export const glossaryFor = (
  isInternal: boolean,
  switches: Partial<FeatureSwitches> = FEATURE_SWITCHES_OFF,
): readonly GlossaryEntry[] => (isInternal ? INTERNAL_GLOSSARY : brandGlossaryFor(switches))

/**
 * VRG-680 — the brand corpus with every entry whose launch switch is off removed. Cached per
 * switch state so the result is a STABLE array (the lookup index below is a WeakMap keyed on
 * corpus identity). Fails closed: called with no switches, every gated entry is hidden.
 */
const BRAND_BY_SWITCHES = new Map<string, readonly GlossaryEntry[]>()
export const brandGlossaryFor = (
  switches: Partial<FeatureSwitches> = FEATURE_SWITCHES_OFF,
): readonly GlossaryEntry[] => {
  const key = JSON.stringify({ referrals: switches.referrals === true })
  let corpus = BRAND_BY_SWITCHES.get(key)
  if (!corpus) {
    corpus = withoutGated(GLOSSARY, switches)
    BRAND_BY_SWITCHES.set(key, corpus)
  }
  return corpus
}

/** Route of the glossary page. Kept here so the anchor rule has one owner. */
export const GLOSSARY_PATH = '/docs/reference/glossary'

/**
 * Deep link to a single term.
 *
 * §44: a tooltip footer links HERE, not to a search results page for the word. The reader
 * is already confused — sending them to a result list asks them to pick the right answer
 * to the question they just failed to answer. Search is the fallback for a term with no
 * entry, not the default for one that has it.
 */
export const glossaryHref = (id: string) => `${GLOSSARY_PATH}#${id}`

interface Index {
  byId: Map<string, GlossaryEntry>
  /** id or alias, case-insensitive. Aliases are unique, enforced at publish time. */
  byAlias: Map<string, GlossaryEntry>
}

/**
 * One index per corpus, built on first use and kept.
 *
 * Keyed on the array itself rather than on a name, so the two corpora cannot be handed
 * the same index by a typo, and a caller that passes some third list (a test fixture)
 * gets a correct index rather than the brand one.
 */
const INDEXES = new WeakMap<readonly GlossaryEntry[], Index>()

const indexOf = (corpus: readonly GlossaryEntry[]): Index => {
  const cached = INDEXES.get(corpus)
  if (cached) return cached

  const byId = new Map<string, GlossaryEntry>()
  const byAlias = new Map<string, GlossaryEntry>()
  for (const e of corpus) {
    byId.set(e.id, e)
    byAlias.set(e.term.toLowerCase(), e)
    for (const a of e.aliases ?? []) byAlias.set(a.toLowerCase(), e)
  }

  const built = { byId, byAlias }
  INDEXES.set(corpus, built)
  return built
}

/**
 * Look an entry up by id.
 *
 * `corpus` defaults to the brand glossary, which keeps every existing non-React caller
 * behaving exactly as before. React call sites should use `useGlossaryTerm` instead, so
 * that staff actually see the internal entries — a default-corpus lookup silently returns
 * `undefined` for them, which is precisely the failure this parameter was added to end.
 */
export const glossaryTerm = (
  id: string,
  corpus: readonly GlossaryEntry[] = GLOSSARY,
): GlossaryEntry | undefined => indexOf(corpus).byId.get(id)

/** Resolve loose user/author input — "Tags", "chip family" — to an entry. */
export const lookupTerm = (
  text: string,
  corpus: readonly GlossaryEntry[] = GLOSSARY,
): GlossaryEntry | undefined => {
  const { byId, byAlias } = indexOf(corpus)
  return byId.get(text) ?? byAlias.get(text.trim().toLowerCase())
}

/** Alphabetical within kind; the page groups by kind and A–Z within each. */
export const glossaryByKind = (
  kind: GlossaryKind,
  corpus: readonly GlossaryEntry[] = GLOSSARY,
): GlossaryEntry[] =>
  corpus.filter(e => e.kind === kind).sort((a, b) => a.term.localeCompare(b.term))

/**
 * Every entry carrying `tag`, alphabetically.
 *
 * Deliberately NOT filtered by kind: the point of the tag is that its members come from
 * several kinds, which is what a section could not express.
 */
export const glossaryByTag = (
  tag: GlossaryTag,
  corpus: readonly GlossaryEntry[] = GLOSSARY,
): GlossaryEntry[] =>
  corpus.filter(e => e.tags?.includes(tag)).sort((a, b) => a.term.localeCompare(b.term))

/**
 * The tag indexes the glossary page renders, in page order, ABOVE the kind groups.
 *
 * `id` is prefixed rather than being the bare tag because it becomes a DOM id on a page
 * whose other section headings are bare kinds — an unprefixed `metric` would be one
 * rename away from colliding with a kind, and the collision would break an anchor
 * silently rather than loudly.
 */
export const GLOSSARY_TAGS: {
  tag: GlossaryTag
  /** DOM id of the index's heading, and the rail's anchor. */
  id: string
  label: string
  blurb: string
}[] = [
  {
    tag: 'metric',
    id: 'tag-metric',
    label: 'Metrics',
    blurb:
      'Every figure the Analytics page can show you, in one list. Each one links to its full definition below.',
  },
]

export const GLOSSARY_KINDS: { kind: GlossaryKind; label: string; blurb: string }[] = [
  { kind: 'term', label: 'Terms', blurb: 'The vocabulary Vera uses for the things you work with.' },
  { kind: 'status', label: 'Statuses', blurb: 'States a record can be in, and what puts it there.' },
  { kind: 'column', label: 'Columns', blurb: 'Fields you will meet in tables and exports.' },
  { kind: 'formula', label: 'Formulas', blurb: 'How calculated figures are worked out.' },
]

/** Every term, A–Z, ignoring kind — the index at the top of the page. */
export const glossaryAlphabetical = (
  corpus: readonly GlossaryEntry[] = GLOSSARY,
): GlossaryEntry[] => [...corpus].sort((a, b) => a.term.localeCompare(b.term))
